import { computed, onMounted, onScopeDispose, toValue, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { useNow } from '@vueuse/core';
import { isString, pick, uniq } from 'lodash';
import { DateTime } from 'luxon';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from '@satellite/composables/queryKeys';
import { useEventHub } from '@satellite/composables/useEventHub';
import { hasCollapsedSocketEventIds } from '@satellite/helpers/socketEventMatch';
import assetVisitService from '@satellite/services/asset-visit-service';

import type { MaybeRefOrGetter } from 'vue';
import type { Maybe } from '@nova/core';
import type { DebouncedSocketEvent } from '@satellite/helpers/socketEventTypes';
import type { Arrival, ArrivalMessageThread } from './types';

const RECENT_ARRIVALS_JOINS = [
  'company||name',
  'appointment||start,status',
  'assetVisitEvents',
  'messageThread||unreadMessagesCount',
  'vehicleObservations',
  'vehicleObservations.assetObservations',
  'idValidationData||inquiryId,firstName,lastName,birthdate,email,phone,status,rejectReasons'
];

const RECENT_ARRIVALS_WINDOW = { days: 1 };
const PRUNE_INTERVAL_MS = 60_000;
const DEFAULT_STALE_TIME_MS = 30_000;

const ARRIVALS_EVENT_HUB_TOPICS = [
  'create-AssetVisit',
  'update-AssetVisit',
  'create-AssetVisitEvent',
  'delete-AssetVisitEvent',
  'update-Appointment',
  'update-MessageThread'
] as const;

/** The fields read off AssetVisit, AssetVisitEvent, Appointment and MessageThread payloads. */
type ArrivalSocketEvent = DebouncedSocketEvent &
  Partial<Omit<ArrivalMessageThread, 'id'>> & {
    warehouseId?: Maybe<string>;
    assetVisitId?: Maybe<string>;
    assetVisit?: Maybe<{ id?: Maybe<string> }>;
  };

type ArrivalsEventHubHandlerMap = Record<
  (typeof ARRIVALS_EVENT_HUB_TOPICS)[number],
  (event: ArrivalSocketEvent) => void
>;

/** Every id a debounced socket event stands for: its own plus the ones it swallowed. */
export function getSocketEventIds(event: Maybe<DebouncedSocketEvent>): string[] {
  const ids = [event?.id, ...(event?.debounce?.ids ?? [])];

  return uniq(ids.filter(isString));
}

export interface UseRecentArrivalsFetcherOptions {
  warehouseId: MaybeRefOrGetter<Maybe<string>>;
  /** False for warehouses without Gate Management, which have no check-ins to show. */
  enabled?: MaybeRefOrGetter<boolean>;
  staleTime?: number;
}

/**
 * Check-ins of the last 24 hours for one warehouse. Live even while nothing renders the list,
 * because the top nav bell counts them on every page, so there is no viewport deferral.
 */
export function useRecentArrivalsFetcher(options: UseRecentArrivalsFetcherOptions) {
  const { warehouseId, enabled = true, staleTime = DEFAULT_STALE_TIME_MS } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();
  const now = useNow({ interval: PRUNE_INTERVAL_MS });

  const effectiveWarehouseId = computed(() => toValue(warehouseId) ?? null);
  const isEnabled = computed(() => Boolean(effectiveWarehouseId.value) && toValue(enabled));

  const arrivalsQuery = useQuery({
    queryKey: computed(() =>
      effectiveWarehouseId.value ? queryKeys.recentArrivals(effectiveWarehouseId.value) : []
    ),
    queryFn: async ({ signal }) => {
      const currentWarehouseId = effectiveWarehouseId.value;
      if (!currentWarehouseId) {
        return [];
      }

      const windowStart = DateTime.utc().minus(RECENT_ARRIVALS_WINDOW).toISO();
      const params = {
        s: {
          $and: [{ warehouseId: currentWarehouseId }, { createDateTime: { $gte: windowStart } }]
        },
        join: RECENT_ARRIVALS_JOINS
      };
      const arrivals = await assetVisitService.getAssetVisitsV2({
        requestConfig: { signal, params }
      });

      return Array.isArray(arrivals) ? (arrivals as Arrival[]) : [];
    },
    enabled: isEnabled,
    staleTime,
    refetchOnWindowFocus: false
  });

  // The query is fetched once and kept live, so visits age out of the window on the client.
  const data = computed((): Arrival[] => {
    const arrivals = arrivalsQuery.data.value ?? [];
    const windowStart = DateTime.fromJSDate(now.value).minus(RECENT_ARRIVALS_WINDOW);

    return arrivals.filter(arrival => DateTime.fromISO(arrival.createDateTime) >= windowStart);
  });

  const isLoading = computed(() => arrivalsQuery.isLoading.value);
  const isFetching = computed(() => arrivalsQuery.isFetching.value);
  const error = computed(() => arrivalsQuery.error.value);

  const arrivalIds = computed(() => new Set(data.value.map(arrival => arrival.id)));
  const hasArrival = (assetVisitId: Maybe<string>) =>
    isString(assetVisitId) && arrivalIds.value.has(assetVisitId);

  const invalidate = () => {
    if (!effectiveWarehouseId.value) {
      return;
    }

    queryClient.invalidateQueries(
      { queryKey: queryKeys.recentArrivals(effectiveWarehouseId.value) },
      DEFAULT_INVALIDATE_OPTIONS
    );
  };

  const refresh = async () => {
    await arrivalsQuery.refetch();
  };

  // The socket handlers follow only the selected warehouse, so the list left behind misses its
  // check-ins from now on. Marked stale, it is refetched on return however soon that comes.
  watch(effectiveWarehouseId, (_newWarehouseId, previousWarehouseId) => {
    if (!previousWarehouseId) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: queryKeys.recentArrivals(previousWarehouseId),
      refetchType: 'none'
    });
  });

  // A batched create is forwarded with one visit's warehouse, so the others may be ours
  const handleCreateAssetVisit = (event: ArrivalSocketEvent) => {
    const isOwnWarehouse = event?.warehouseId === effectiveWarehouseId.value;
    if (!isOwnWarehouse && !hasCollapsedSocketEventIds(event)) {
      return;
    }

    invalidate();
  };

  const handleUpdateAssetVisit = (event: ArrivalSocketEvent) => {
    if (!getSocketEventIds(event).some(hasArrival)) {
      return;
    }

    invalidate();
  };

  const handleAssetVisitEventChange = (event: ArrivalSocketEvent) => {
    if (!hasArrival(event?.assetVisitId) && !hasCollapsedSocketEventIds(event)) {
      return;
    }

    invalidate();
  };

  const handleUpdateAppointment = (event: ArrivalSocketEvent) => {
    const appointmentIds = getSocketEventIds(event);
    const hasLinkedArrival = data.value.some(arrival =>
      appointmentIds.includes(arrival.appointmentId ?? '')
    );
    if (!hasLinkedArrival && !hasArrival(event?.assetVisit?.id)) {
      return;
    }

    invalidate();
  };

  const handleUpdateMessageThread = (event: ArrivalSocketEvent) => {
    // A collapsed payload describes another thread, so only a refetch is safe
    if (hasCollapsedSocketEventIds(event)) {
      invalidate();
      return;
    }

    if (!effectiveWarehouseId.value || !hasArrival(event?.assetVisitId)) {
      return;
    }

    const threadChanges = pick(event, ['id', 'unreadMessagesCount', 'lastChangedDateTime']);
    queryClient.setQueryData<Arrival[]>(
      queryKeys.recentArrivals(effectiveWarehouseId.value),
      arrivals =>
        arrivals?.map(arrival => {
          if (arrival.id !== event.assetVisitId) {
            return arrival;
          }

          const messageThread = {
            ...arrival.messageThread,
            ...threadChanges
          } as ArrivalMessageThread;
          return { ...arrival, messageThread };
        })
    );
  };

  const eventHubHandlers: ArrivalsEventHubHandlerMap = {
    'create-AssetVisit': handleCreateAssetVisit,
    'update-AssetVisit': handleUpdateAssetVisit,
    'create-AssetVisitEvent': handleAssetVisitEventChange,
    'delete-AssetVisitEvent': handleAssetVisitEventChange,
    'update-Appointment': handleUpdateAppointment,
    'update-MessageThread': handleUpdateMessageThread
  };

  onMounted(() => {
    for (const topic of ARRIVALS_EVENT_HUB_TOPICS) {
      eventHub.on(topic, eventHubHandlers[topic]);
    }
  });

  onScopeDispose(() => {
    for (const topic of ARRIVALS_EVENT_HUB_TOPICS) {
      eventHub.off(topic, eventHubHandlers[topic]);
    }
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    query: arrivalsQuery
  };
}
